import { store, getElement } from '@wordpress/interactivity';
import '../../front.scss';

// Shared IntersectionObserver, created lazily on first use.
let sharedObserver = null;

// Read the animation configuration from an element. ``data-amp-animate`` is
// the single source of animation configuration for both the Animate In block
// and HOC-animated blocks.
const getAnimationConfig = (element) => {
  const configAttr = element.getAttribute('data-amp-animate');
  if (!configAttr) {
    return {};
  }

  try {
    return JSON.parse(configAttr);
  } catch (error) {
    console.error('Failed to parse animation config:', error, element);
    return {};
  }
};

// Run one animation cycle for a block.
const runAnimationCycle = (block, config) => {
  if (!block.__ampInViewTriggered) {
    return;
  }

  block.classList.add('is-animating');

  const animationDuration = parseFloat(config.animationDuration || 1);
  const animationDelay = parseFloat(config.animationDelay || 0);
  const totalDuration = animationDuration + animationDelay;

  setTimeout(() => {
    if (!block.__ampInViewTriggered) {
      return;
    }

    block.classList.remove('is-animating');

    if (config.animationRepeat) {
      const repeatDelay =
        config.animationRepeatDelay > 0
          ? config.animationRepeatDelay * 1000
          : 0;
      setTimeout(runAnimationCycle, repeatDelay, block, config);
    }
  }, totalDuration * 1000);
};

// Mark a block as in view and start its animation.
const markBlockInView = (block) => {
  if (!block.__ampInViewTriggered) {
    block.__ampInViewTriggered = true;
    block.classList.add('is-in-view', 'is-animating');
    runAnimationCycle(block, getAnimationConfig(block));
  }
};

// Get (or create) the shared IntersectionObserver.
const getSharedObserver = () => {
  if (sharedObserver) {
    return sharedObserver;
  }

  sharedObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const block = entry.target;
        const config = getAnimationConfig(block);
        const threshold = !isNaN(parseFloat(config.animationThreshold))
          ? parseFloat(config.animationThreshold)
          : 0.1;

        if (
          entry.intersectionRatio >= threshold &&
          (threshold > 0 || entry.intersectionRatio > 0)
        ) {
          markBlockInView(block);

          // Non-reverse animations only need to trigger once.
          if (!config.animationReverse) {
            sharedObserver.unobserve(block);
          }
        } else if (config.animationReverse && block.__ampInViewTriggered) {
          block.classList.remove('is-in-view', 'is-animating');
          block.__ampInViewTriggered = false;
        }
      });
    },
    { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
  );

  return sharedObserver;
};

// Interactivity API store driving both the Animate In block and HOC-animated
// blocks. Each animated element registers itself via its namespaced
// ``data-wp-init`` callback.
store('amplify_storytelling_animateIn', {
  state: {},
  actions: {},
  callbacks: {
    init: () => {
      const element = getElement()?.ref;
      if (!element) {
        return;
      }

      getSharedObserver().observe(element);
    },
  },
});
